import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/Navbar.css';
import logo from '../assets/logo.png';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Scroll behavior: glass on scroll + auto-hide on scroll down
  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      setScrolled(currentScroll > 30);

      // Hide on scroll down, show on scroll up (after 200px)
      if (currentScroll > 200) {
        if (currentScroll > lastScrollY) {
          setHidden(true);
        } else {
          setHidden(false);
        }
      } else {
        setHidden(false);
      }
      setLastScrollY(currentScroll);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const scrollToSection = (targetId) => {
    if (targetId === 'contact') {
      const element = document.querySelector('footer');
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else {
      const element = document.getElementById(`${targetId}-section`);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavLinkClick = (targetId) => {
    if (targetId === 'careers') {
      navigate('/careers');
    } else if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: targetId } });
    } else {
      scrollToSection(targetId);
    }
    setIsMobileMenuOpen(false);
  };

  const handleAboutUsClick = () => {
    navigate('/AboutUs');
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (location.pathname === '/' && location.state?.scrollTo) {
      setTimeout(() => scrollToSection(location.state.scrollTo), 100);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const navItems = [
    { label: 'Careers', target: 'careers' },
    { label: 'About', target: 'about' },
    { label: 'Services', target: 'services' },
    { label: 'Contact', target: 'contact' }
  ];

  return (
    <>
      <nav className={`luxe-navbar ${scrolled ? 'is-scrolled' : ''} ${hidden ? 'is-hidden' : ''}`}>
        <div className="luxe-nav-inner">
          {/* Brand */}
          <div className="luxe-brand" onClick={() => navigate('/')}>
            <img src={logo} alt="HotelPlacement.ai" className="luxe-logo" />
          </div>

          {/* Desktop Links */}
          <div className="luxe-nav-links">
            {navItems.map((item) => (
              <button
                key={item.target}
                className="luxe-nav-link"
                onClick={() => handleNavLinkClick(item.target)}
              >
                <span>{item.label}</span>
              </button>
            ))}
            <button className="luxe-nav-cta" onClick={handleAboutUsClick}>
              <span>About Us</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`luxe-burger ${isMobileMenuOpen ? 'is-open' : ''}`}
            onClick={handleMobileMenuToggle}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Mobile Overlay Menu */}
      <div className={`luxe-mobile-overlay ${isMobileMenuOpen ? 'is-active' : ''}`}>
        <div className="luxe-mobile-content">
          <div className="luxe-mobile-links">
            {navItems.map((item, i) => (
              <button
                key={item.target}
                className="luxe-mobile-link"
                onClick={() => handleNavLinkClick(item.target)}
                style={{ transitionDelay: `${i * 70 + 200}ms` }}
              >
                <span className="luxe-mobile-num">0{i + 1}</span>
                <span className="luxe-mobile-text">{item.label}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            ))}
            <button
              className="luxe-mobile-link luxe-mobile-cta"
              onClick={handleAboutUsClick}
              style={{ transitionDelay: `${navItems.length * 70 + 200}ms` }}
            >
              <span className="luxe-mobile-num">05</span>
              <span className="luxe-mobile-text">About Us</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>

          <div className="luxe-mobile-footer">
            <span>HotelPlacement.ai</span>
            <span>Dubai · UAE</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;